<template>
  <v-main>
    <v-container fluid class="pa-4">

      <!-- Header -->
      <div class="error-logs-header mb-4">
        <div class="header-content">
          <div class="d-flex align-center">
            <div class="header-icon-wrap mr-3">
              <v-icon color="white" size="26">mdi-alert-circle</v-icon>
            </div>
            <div>
              <h2 class="white--text font-weight-bold" style="font-size:22px; letter-spacing:0.3px;">Error Logs</h2>
              <span class="white--text" style="font-size:12px; opacity:0.75;">Backend exceptions &amp; frontend error tracking</span>
            </div>
          </div>
          <div class="d-flex align-center" style="gap:10px;">
            <!-- Summary severity chips -->
            <div class="d-flex align-center" style="gap:8px;">
              <div class="header-stat-chip header-stat-chip--critical" @click="setSeverity('critical')">
                <v-icon x-small class="mr-1">mdi-skull</v-icon>Critical
              </div>
              <div class="header-stat-chip header-stat-chip--error" @click="setSeverity('error')">
                <v-icon x-small class="mr-1">mdi-alert</v-icon>Error
              </div>
              <div class="header-stat-chip header-stat-chip--warning" @click="setSeverity('warning')">
                <v-icon x-small class="mr-1">mdi-alert-outline</v-icon>Warning
              </div>
            </div>
            <v-chip dark outlined class="white--text" style="border-color:rgba(255,255,255,0.4);">
              <v-icon left small>mdi-database</v-icon>{{ count }} records
            </v-chip>
          </div>
        </div>
      </div>

      <!-- Filter Card -->
      <v-card elevation="3" rounded="xl" class="mb-4">
        <v-card-text class="pb-2">
          <v-row dense align="center">
            <v-col md="4" sm="12">
              <v-text-field
                v-model="filter.search_keyword"
                label="Search message, error type, URL..."
                prepend-inner-icon="mdi-magnify"
                outlined dense rounded clearable hide-details
                background-color="grey lighten-5"
                @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                @keydown.enter="(page = 1), getData()"
              ></v-text-field>
            </v-col>
            <v-col md="2" sm="6">
              <v-autocomplete
                v-model="filter.severity" :items="severityOptions"
                label="Severity" outlined dense rounded clearable hide-details
                prepend-inner-icon="mdi-alert-circle-outline"
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-autocomplete>
            </v-col>
            <v-col md="2" sm="6">
              <v-autocomplete
                v-model="filter.source" :items="sourceOptions"
                label="Source" outlined dense rounded clearable hide-details
                prepend-inner-icon="mdi-source-branch"
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-autocomplete>
            </v-col>
            <v-col md="2" sm="6">
              <v-text-field
                v-model="filter.from_date" label="From"
                type="date" outlined dense rounded clearable hide-details
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-text-field>
            </v-col>
            <v-col md="2" sm="6">
              <v-text-field
                v-model="filter.to_date" label="To"
                type="date" outlined dense rounded clearable hide-details
                background-color="grey lighten-5"
                @change="(page = 1), getData()"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Second row filters -->
          <div class="d-flex align-center mt-3 mb-1" style="gap:10px; flex-wrap:wrap;">
            <!-- Resolved status toggle pills -->
            <div
              class="toggle-pill"
              :class="filter.is_resolved === 'false' ? 'toggle-pill--active-red' : ''"
              @click="toggleResolved('false')"
            >
              <v-icon small :color="filter.is_resolved === 'false' ? 'white' : 'grey darken-1'" class="mr-1">mdi-close-circle-outline</v-icon>
              <span>Unresolved</span>
            </div>
            <div
              class="toggle-pill"
              :class="filter.is_resolved === 'true' ? 'toggle-pill--active-green' : ''"
              @click="toggleResolved('true')"
            >
              <v-icon small :color="filter.is_resolved === 'true' ? 'white' : 'grey darken-1'" class="mr-1">mdi-check-circle-outline</v-icon>
              <span>Resolved</span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Table Card -->
      <v-card elevation="3" rounded="xl">
        <v-progress-linear v-if="isLoading" indeterminate color="red darken-1" height="3" rounded></v-progress-linear>

        <v-card-text class="pa-0">
          <v-simple-table class="error-table">
            <thead>
              <tr class="table-header-row">
                <th class="pl-5">#</th>
                <th>Error Type</th>
                <th>Message</th>
                <th>Severity</th>
                <th>Source</th>
                <th>File : Line</th>
                <th>URL</th>
                <th class="text-center">Resolved</th>
                <th>Time</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!isLoading && logs.length === 0">
                <td colspan="10" class="text-center py-10">
                  <v-icon size="48" color="grey lighten-2">mdi-check-circle-outline</v-icon>
                  <div class="grey--text mt-2 text-body-2">No error logs found</div>
                </td>
              </tr>
              <tr
                v-else
                v-for="(log, idx) in logs" :key="log.id"
                class="error-row"
                :class="rowClass(log)"
              >
                <td class="pl-5 text-caption grey--text" style="width:40px;">
                  {{ (page - 1) * (rowsPerPage === 'All' ? 0 : rowsPerPage) + idx + 1 }}
                </td>
                <td>
                  <span class="error-type-badge" :class="'error-type--' + sourceKey(log.source)">
                    {{ shortErrorType(log.error_type) }}
                  </span>
                </td>
                <td style="max-width:280px;">
                  <div class="message-text" :title="log.message">
                    {{ log.message ? log.message.substring(0, 70) + (log.message.length > 70 ? '…' : '') : '—' }}
                  </div>
                </td>
                <td>
                  <span class="severity-badge" :class="'severity-' + log.severity">
                    <span class="severity-dot"></span>{{ log.severity }}
                  </span>
                </td>
                <td>
                  <span class="source-badge" :class="log.source === 'frontend' ? 'source-frontend' : 'source-backend'">
                    <v-icon x-small class="mr-1">{{ log.source === 'frontend' ? 'mdi-vuejs' : 'mdi-server' }}</v-icon>
                    {{ log.source }}
                  </span>
                </td>
                <td>
                  <span v-if="log.file" class="file-chip">
                    {{ log.file.split('/').pop() }}<span class="file-line">:{{ log.line }}</span>
                  </span>
                  <span v-else class="grey--text text-caption">—</span>
                </td>
                <td style="max-width:220px;">
                  <div class="url-text" :title="log.url">{{ log.url ? urlPath(log.url) : '—' }}</div>
                </td>
                <td class="text-center">
                  <div class="resolved-indicator" :class="log.is_resolved ? 'resolved-indicator--yes' : 'resolved-indicator--no'">
                    <v-icon small :color="log.is_resolved ? '#2e7d32' : '#c62828'">
                      {{ log.is_resolved ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </div>
                </td>
                <td class="text-caption grey--text text--darken-1" style="white-space:nowrap;">
                  {{ formatDate(log.created_at) }}
                </td>
                <td class="text-center">
                  <div class="d-flex align-center justify-center" style="gap:2px;">
                    <v-btn icon small color="primary" :to="'/error-logs/' + log.id" class="action-btn">
                      <v-icon small>mdi-arrow-right-circle-outline</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="!log.is_resolved"
                      icon small color="green"
                      class="action-btn"
                      :loading="log._resolving"
                      @click="markResolved(log)"
                    >
                      <v-icon small>mdi-check-circle-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <!-- Pagination -->
        <v-divider></v-divider>
        <div class="d-flex align-center px-4 py-3" style="position: relative;">
          <div class="d-flex align-center" style="gap: 8px; position: absolute; left: 16px;">
            <span class="text-caption grey--text">Rows per page:</span>
            <v-select
              v-model="rowsPerPage" :items="rowsPerPageOptions" dense outlined hide-details
              style="max-width: 90px;" @change="(page = 1), getData()"
            ></v-select>
          </div>
          <v-pagination
            v-if="rowsPerPage !== 'All'" rounded v-model="page"
            :length="paginationLength" :total-visible="10" class="mx-auto"
          ></v-pagination>
        </div>
      </v-card>

    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "ErrorLogsPage",
  data() {
    return {
      logs: [],
      count: 0,
      page: 1,
      rowsPerPage: 20,
      rowsPerPageOptions: [10, 20, 50, 100, "All"],
      paginationLength: 0,
      isLoading: false,
      filter: {
        search_keyword: null,
        severity: null,
        source: null,
        is_resolved: null,
        from_date: null,
        to_date: null,
      },
      severityOptions: ["critical", "error", "warning"],
      sourceOptions: ["backend", "frontend"],
      resolvedOptions: [
        { text: "Unresolved", value: "false" },
        { text: "Resolved", value: "true" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  watch: {
    page() { this.getData(); },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const isAll = this.rowsPerPage === "All";
        let params = [];
        if (!isAll) params.push(`page=${this.page}&rowsPerPage=${this.rowsPerPage}`);
        if (this.filter.search_keyword) params.push(`search_keyword=${encodeURIComponent(this.filter.search_keyword)}`);
        if (this.filter.severity) params.push(`severity=${this.filter.severity}`);
        if (this.filter.source) params.push(`source=${this.filter.source}`);
        if (this.filter.is_resolved !== null && this.filter.is_resolved !== undefined && this.filter.is_resolved !== '') {
          params.push(`is_resolved=${this.filter.is_resolved}`);
        }
        if (this.filter.from_date) params.push(`from_date=${this.filter.from_date}`);
        if (this.filter.to_date) params.push(`to_date=${this.filter.to_date}`);

        const { data } = await axios.get(`/error_logs?${params.join("&")}`);
        this.logs = data.data.data || data.data;
        this.count = data.count;
        if (!isAll) this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async markResolved(log) {
      this.$set(log, '_resolving', true);
      try {
        await axios.post(`/error_logs/${log.id}/resolve`);
        log.is_resolved = true;
      } catch (e) {
        console.error(e);
      } finally {
        this.$set(log, '_resolving', false);
      }
    },
    setSeverity(sev) {
      this.filter.severity = this.filter.severity === sev ? null : sev;
      this.page = 1;
      this.getData();
    },
    toggleResolved(val) {
      this.filter.is_resolved = this.filter.is_resolved === val ? null : val;
      this.page = 1;
      this.getData();
    },
    rowClass(log) {
      if (!log.is_resolved && log.severity === 'critical') return 'error-row--critical';
      if (!log.is_resolved && log.severity === 'error') return 'error-row--error';
      if (log.is_resolved) return 'error-row--resolved';
      return '';
    },
    sourceKey(source) {
      return source === 'frontend' ? 'frontend' : 'backend';
    },
    shortErrorType(type) {
      if (!type) return '—';
      return type.split('\\').pop();
    },
    urlPath(url) {
      try { return new URL(url).pathname; } catch { return url; }
    },
    formatDate(dt) {
      if (!dt) return "—";
      return new Date(dt).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
    },
  },
};
</script>

<style scoped>
/* ===== Header ===== */
.error-logs-header {
  background: linear-gradient(135deg, #7f0000 0%, #b71c1c 40%, #c62828 100%);
  border-radius: 16px;
  padding: 20px 24px;
}
.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.header-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255,255,255,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-stat-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  border: 1.5px solid rgba(255,255,255,0.35);
  color: white;
}
.header-stat-chip:hover { opacity: 0.85; }
.header-stat-chip--critical { background: rgba(183, 28, 28, 0.7); }
.header-stat-chip--error    { background: rgba(230, 81, 0, 0.7); }
.header-stat-chip--warning  { background: rgba(245, 127, 23, 0.6); }

/* ===== Toggle Pills ===== */
.toggle-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid #d0d0d0;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  background: #f7f7f7;
  transition: all 0.2s;
  user-select: none;
}
.toggle-pill:hover { background: #ebebeb; }
.toggle-pill--active-red   { background: #c62828; border-color: #c62828; color: white; }
.toggle-pill--active-green { background: #2e7d32; border-color: #2e7d32; color: white; }

/* ===== Table ===== */
.error-table thead tr th {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #666 !important;
  background: #fafafa !important;
  border-bottom: 2px solid #eeeeee !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}
.error-row {
  border-left: 3px solid transparent;
  transition: background 0.15s;
}
.error-row:hover { background: #fff5f5 !important; }
.error-row--critical { border-left-color: #b71c1c !important; background: #fff5f5; }
.error-row--error    { border-left-color: #e65100 !important; background: #fffbf5; }
.error-row--resolved { border-left-color: #a5d6a7 !important; opacity: 0.7; }
.error-row td { padding: 10px 14px !important; vertical-align: middle; }

/* ===== Error Type Badge ===== */
.error-type-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  font-family: 'Courier New', monospace;
}
.error-type--backend  { background: #fce4ec; color: #b71c1c; }
.error-type--frontend { background: #f3e5f5; color: #6a1b9a; }

/* ===== Severity Badge ===== */
.severity-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  gap: 5px;
}
.severity-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
.severity-critical { background: #fce4ec; color: #b71c1c; }
.severity-critical .severity-dot { background: #b71c1c; }
.severity-error    { background: #fff3e0; color: #e65100; }
.severity-error    .severity-dot { background: #e65100; }
.severity-warning  { background: #fffde7; color: #f57f17; }
.severity-warning  .severity-dot { background: #f57f17; }

/* ===== Source Badge ===== */
.source-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}
.source-backend  { background: #e3f2fd; color: #1565c0; }
.source-frontend { background: #f3e5f5; color: #6a1b9a; }

/* ===== File chip ===== */
.file-chip {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  background: #f5f5f5;
  color: #555;
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
}
.file-line { color: #999; }

/* ===== Message / URL ===== */
.message-text {
  font-size: 12px;
  color: #444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 270px;
}
.url-text {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 210px;
  font-family: 'Courier New', monospace;
}

/* ===== Resolved Indicator ===== */
.resolved-indicator {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.resolved-indicator--yes { background: #e8f5e9; }
.resolved-indicator--no  { background: #fce4ec; }

/* ===== Action buttons ===== */
.action-btn { opacity: 0.6; transition: opacity 0.15s; }
.error-row:hover .action-btn { opacity: 1; }
</style>
